<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BoardsAPI from 'kanban/api/boards.js';
import CatalogAPI from 'dashboard/api/catalog';
import { RFM_SEGMENTS } from 'rfm/constants/segments.js';
import { segmentLabel } from 'rfm/constants/segmentLabels.js';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';
import catalog from 'flow_builder/constants/catalog.json';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  nodeId: { type: String, default: null },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const resolveLabel = label =>
  label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const getUpstreamVariables = inject('getUpstreamVariables', () => []);
const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...flowDynamicVariables.value];
});

// ── Variable picker (for dynamic_variable inputType) ─────────────
const openPickerKey = ref(null);
const varSearch = ref('');

const filteredVars = computed(() => {
  const q = varSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(
    v =>
      resolveLabel(v.label).toLowerCase().includes(q) ||
      v.key.toLowerCase().includes(q)
  );
});

function togglePicker(key) {
  openPickerKey.value = openPickerKey.value === key ? null : key;
  varSearch.value = '';
}

function insertDynVar(index, key) {
  patch(index, { custom_attr_key: `{{${key}}}` });
  openPickerKey.value = null;
}

const store = useStore();
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');

const allSteps = ref([]);
const catalogProducts = ref([]);

async function loadCatalogProducts(query = '') {
  try {
    const { data } = await CatalogAPI.getProducts({ query, perPage: 50 });
    catalogProducts.value = (data?.products ?? []).map(p => ({
      id: p.id,
      name: p.title,
    }));
  } catch {
    catalogProducts.value = [];
  }
}

async function loadKanbanSteps() {
  try {
    const { data } = await BoardsAPI.get();
    const boards = data?.boards ?? [];
    const results = await Promise.all(
      boards.map(b =>
        BoardsAPI.getOne(b.id)
          .then(r => ({ board: b, steps: r.data?.steps ?? [] }))
          .catch(() => ({ board: b, steps: [] }))
      )
    );
    allSteps.value = results.flatMap(({ board, steps }) =>
      steps.map(s => ({ id: String(s.id), name: `${board.name} › ${s.name}` }))
    );
  } catch {
    allSteps.value = [];
  }
}

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
  loadKanbanSteps();
  loadCatalogProducts();
});

// ── Operator sets (labels resolved from the shared catalog.json) ──
const NO_VALUE_OPS = new Set(['is_present', 'is_empty']);

const OPERATOR_SETS = computed(() =>
  Object.fromEntries(
    Object.entries(catalog.conditionOperatorSets).map(([name, ops]) => [
      name,
      ops.map(o => ({ value: o.value, label: t(o.label) })),
    ])
  )
);

// ── Runtime value resolvers, keyed by catalog `optionsSource` ─────
const STATUS_OPTIONS = computed(() => [
  { id: 'open', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.STATUS_OPEN') },
  {
    id: 'resolved',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.STATUS_RESOLVED'),
  },
  {
    id: 'pending',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.STATUS_PENDING'),
  },
  {
    id: 'snoozed',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.STATUS_SNOOZED'),
  },
]);
const PRIORITY_OPTIONS = computed(() => [
  { id: 'none', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.PRIORITY_NONE') },
  { id: 'low', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.PRIORITY_LOW') },
  {
    id: 'medium',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.PRIORITY_MEDIUM'),
  },
  { id: 'high', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.PRIORITY_HIGH') },
  {
    id: 'urgent',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.PRIORITY_URGENT'),
  },
]);
const ORDER_FINANCIAL_OPTIONS = computed(() => [
  {
    id: 'pending',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FINANCIAL_PENDING'),
  },
  {
    id: 'authorized',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FINANCIAL_AUTHORIZED'),
  },
  { id: 'paid', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FINANCIAL_PAID') },
  {
    id: 'partially_paid',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FINANCIAL_PARTIALLY_PAID'),
  },
  {
    id: 'refunded',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FINANCIAL_REFUNDED'),
  },
  {
    id: 'partially_refunded',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FINANCIAL_PARTIALLY_REFUNDED'),
  },
  {
    id: 'cancelled',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FINANCIAL_CANCELLED'),
  },
]);
const ORDER_FULFILLMENT_OPTIONS = computed(() => [
  {
    id: 'unfulfilled',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FULFILLMENT_UNFULFILLED'),
  },
  {
    id: 'partially_shipped',
    name: t(
      'FLOW_BUILDER.ACTION_FORMS.CONDITION.FULFILLMENT_PARTIALLY_SHIPPED'
    ),
  },
  {
    id: 'shipped',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FULFILLMENT_SHIPPED'),
  },
  {
    id: 'delivered',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FULFILLMENT_DELIVERED'),
  },
  {
    id: 'returned',
    name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.FULFILLMENT_RETURNED'),
  },
]);
const DAY_OPTIONS = computed(() => [
  { id: '0', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.DAY_SUNDAY') },
  { id: '1', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.DAY_MONDAY') },
  { id: '2', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.DAY_TUESDAY') },
  { id: '3', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.DAY_WEDNESDAY') },
  { id: '4', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.DAY_THURSDAY') },
  { id: '5', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.DAY_FRIDAY') },
  { id: '6', name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.DAY_SATURDAY') },
]);

const OPTION_RESOLVERS = {
  status: () => STATUS_OPTIONS.value,
  priority: () => PRIORITY_OPTIONS.value,
  inboxes: () => (inboxes.value ?? []).map(i => ({ id: i.id, name: i.name })),
  agents: () => (agents.value ?? []).map(a => ({ id: a.id, name: a.name })),
  teams: () =>
    (teams.value ?? []).map(team => ({ id: team.id, name: team.name })),
  conversationLabels: () =>
    (labels.value ?? []).map(l => ({ id: l.id, name: l.title })),
  contactLabels: () =>
    (labels.value ?? []).map(l => ({ id: l.title, name: l.title })),
  rfmSegments: () =>
    RFM_SEGMENTS.map(s => ({ id: s.key, name: segmentLabel(t, s.key) })),
  messageType: () => [
    {
      id: 'incoming',
      name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.MESSAGE_INCOMING'),
    },
    {
      id: 'outgoing',
      name: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.MESSAGE_OUTGOING'),
    },
  ],
  catalogProducts: () => catalogProducts.value,
  orderFinancial: () => ORDER_FINANCIAL_OPTIONS.value,
  orderFulfillment: () => ORDER_FULFILLMENT_OPTIONS.value,
  kanbanSteps: () => allSteps.value,
  dayOfWeek: () => DAY_OPTIONS.value,
};

// ── Attribute catalog (descriptors from the shared catalog.json) ──
const ATTRIBUTES = computed(() =>
  catalog.conditionAttributes.map(a => ({
    key: a.key,
    label: t(a.label),
    group: t(a.group),
    inputType: a.inputType,
    operators: OPERATOR_SETS.value[a.operators] ?? [],
    options: OPTION_RESOLVERS[a.optionsSource] ?? (() => []),
  }))
);

const upstreamVarAttributes = computed(() =>
  getUpstreamVariables(props.nodeId).map(v => ({
    key: v.key,
    label: v.label,
    group: t('FLOW_BUILDER.ACTION_FORMS.CONDITION.GROUP_VARIABLES'),
    inputType: 'text',
    operators: OPERATOR_SETS.value.textVar,
    options: () => [],
  }))
);

const allAttributes = computed(() => [
  ...ATTRIBUTES.value,
  ...upstreamVarAttributes.value,
]);

// Build flat options list with group headers.
// Duplicate keys (same field in different groups) get a group-qualified id so Vue doesn't warn.
// onSelectAttr strips the qualifier before storing the real key.
const ATTRIBUTE_OPTIONS = computed(() => {
  const attrs = allAttributes.value;
  const groups = {};
  attrs.forEach(a => {
    if (!groups[a.group]) groups[a.group] = [];
    groups[a.group].push(a);
  });
  const keyCounts = {};
  attrs.forEach(a => {
    keyCounts[a.key] = (keyCounts[a.key] || 0) + 1;
  });
  const result = [];
  Object.entries(groups).forEach(([group, groupAttrs]) => {
    result.push({ id: `__group__${group}`, name: group, isGroup: true });
    groupAttrs.forEach(a => {
      const id = keyCounts[a.key] > 1 ? `${a.key}::${group}` : a.key;
      result.push({ id, name: a.label });
    });
  });
  return result;
});

const QUERY_OPTIONS = computed(() => [
  { id: 'and', name: t('FLOW_BUILDER.COMMON.AND') },
  { id: 'or', name: t('FLOW_BUILDER.COMMON.OR') },
]);

// ── Helpers ──────────────────────────────────────────────────────
function attrDef(key) {
  return allAttributes.value.find(a => a.key === key) ?? ATTRIBUTES.value[0];
}
function selectedAttr(key) {
  const attr = allAttributes.value.find(a => a.key === key);
  return attr ? { id: attr.key, name: attr.label } : null;
}
function opOptions(key) {
  return attrDef(key).operators.map(o => ({ id: o.value, name: o.label }));
}
function valOptions(key) {
  return attrDef(key).options();
}
function needsValue(cond) {
  return !NO_VALUE_OPS.has(cond.filter_operator);
}

function selectedOp(cond) {
  return (
    opOptions(cond.attribute_key).find(o => o.id === cond.filter_operator) ??
    null
  );
}
function selectedVal(cond) {
  return valOptions(cond.attribute_key).find(o => o.id === cond.values) ?? null;
}

// ── Conditions state ─────────────────────────────────────────────
const conditions = computed(() => props.modelValue?.conditions ?? []);

function emitConditions(list) {
  const updated = list.map((c, i) => ({
    ...c,
    query_operator: i < list.length - 1 ? c.query_operator || 'and' : null,
  }));
  emit('update:modelValue', { ...props.modelValue, conditions: updated });
}

function addCondition() {
  const first = ATTRIBUTES.value[0];
  emitConditions([
    ...conditions.value,
    {
      attribute_key: first.key,
      filter_operator: first.operators[0].value,
      values: '',
      query_operator: 'and',
    },
  ]);
}

function removeCondition(index) {
  emitConditions(conditions.value.filter((_, i) => i !== index));
}

function patch(index, obj) {
  emitConditions(
    conditions.value.map((c, i) => (i === index ? { ...c, ...obj } : c))
  );
}

function onSelectAttr(index, val) {
  const realKey = val?.id?.replace(/::.+$/, '');
  const attr = allAttributes.value.find(a => a.key === realKey);
  if (!attr) return;
  patch(index, {
    attribute_key: attr.key,
    filter_operator: attr.operators[0].value,
    values: attr.inputType === 'multiSelect' ? [] : '',
    custom_attr_key: '',
  });
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <template v-for="(cond, i) in conditions" :key="i">
      <!-- AND / OR connector -->
      <div v-if="i > 0" class="flex items-center gap-2">
        <div class="h-px flex-1 bg-n-weak" />
        <div class="multiselect-wrap--small w-16">
          <MultiselectDropdown
            :options="QUERY_OPTIONS"
            :selected-item="
              QUERY_OPTIONS.find(
                o => o.id === (conditions[i - 1].query_operator || 'and')
              )
            "
            :has-thumbnail="false"
            :hide-search="true"
            @select="v => patch(i - 1, { query_operator: v?.id ?? 'and' })"
          />
        </div>
        <div class="h-px flex-1 bg-n-weak" />
      </div>

      <!-- Condition card -->
      <div class="rounded-xl border border-n-weak bg-n-solid-1">
        <!-- Row 1: "Se" + attribute + delete -->
        <div
          class="flex items-center gap-1.5 px-2 py-1.5 bg-n-solid-2 border-b border-n-weak rounded-t-xl"
        >
          <span class="text-[11px] font-medium text-n-slate-9 shrink-0">{{
            t('FLOW_BUILDER.COMMON.IF')
          }}</span>

          <div class="multiselect-wrap--small flex-1">
            <MultiselectDropdown
              :options="ATTRIBUTE_OPTIONS"
              :selected-item="selectedAttr(cond.attribute_key)"
              :multiselector-placeholder="
                t('FLOW_BUILDER.ACTION_FORMS.CONDITION.ATTRIBUTE_PLACEHOLDER')
              "
              :input-placeholder="
                t('FLOW_BUILDER.ACTION_FORMS.CONDITION.SEARCH_ATTRIBUTE')
              "
              :has-thumbnail="false"
              @select="v => onSelectAttr(i, v)"
            />
          </div>

          <button
            type="button"
            class="i-lucide-x size-3 text-n-slate-9 hover:text-n-ruby-11 shrink-0 transition-colors"
            @click="removeCondition(i)"
          />
        </div>

        <!-- Custom attribute key -->
        <div
          v-if="attrDef(cond.attribute_key).inputType === 'custom_attr'"
          class="px-2 py-2 border-b border-n-weak"
        >
          <input
            type="text"
            class="w-full h-8 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand !mb-0"
            :value="cond.custom_attr_key ?? ''"
            :placeholder="
              t('FLOW_BUILDER.ACTION_FORMS.CONDITION.CUSTOM_ATTR_PLACEHOLDER')
            "
            @input="patch(i, { custom_attr_key: $event.target.value })"
          />
        </div>

        <!-- Dynamic variable key picker -->
        <div
          v-else-if="
            attrDef(cond.attribute_key).inputType === 'dynamic_variable'
          "
          class="px-2 py-2 border-b border-n-weak"
        >
          <div
            class="relative flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand"
          >
            <input
              type="text"
              class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
              :value="cond.custom_attr_key ?? ''"
              :placeholder="
                t('FLOW_BUILDER.ACTION_FORMS.CONDITION.DYNAMIC_VAR_PLACEHOLDER')
              "
              @input="patch(i, { custom_attr_key: $event.target.value })"
            />
            <button
              type="button"
              class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
              :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
              @click.stop="togglePicker(`dynvar_${i}`)"
            >
              <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
            </button>
            <div
              v-if="openPickerKey === `dynvar_${i}`"
              class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
            >
              <div class="px-2 pt-2 pb-1">
                <div
                  class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
                >
                  <span
                    class="i-lucide-search size-3 text-n-slate-9 shrink-0"
                  />
                  <input
                    v-model="varSearch"
                    type="text"
                    class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                    :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                    autofocus
                  />
                </div>
              </div>
              <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
                <button
                  v-for="v in filteredVars"
                  :key="v.key"
                  type="button"
                  class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                  @click="insertDynVar(i, v.key)"
                >
                  {{ resolveLabel(v.label) }}
                </button>
                <p
                  v-if="!filteredVars.length"
                  class="px-3 py-2 text-xs text-n-slate-9 italic"
                >
                  {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 2: operator + value -->
        <div
          class="flex gap-1.5 px-2 py-1.5"
          :class="[
            attrDef(cond.attribute_key).inputType === 'product_select'
              ? 'flex-col'
              : 'items-start',
          ]"
        >
          <!-- Operator -->
          <div
            class="multiselect-wrap--small shrink-0"
            :class="[
              attrDef(cond.attribute_key).inputType === 'product_select'
                ? 'w-full'
                : 'w-[48%]',
            ]"
          >
            <MultiselectDropdown
              :options="opOptions(cond.attribute_key)"
              :selected-item="selectedOp(cond)"
              :multiselector-placeholder="t('FLOW_BUILDER.COMMON.OPERATOR')"
              :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              :has-thumbnail="false"
              :hide-search="true"
              @select="v => patch(i, { filter_operator: v?.id ?? '' })"
            />
          </div>

          <!-- Value: hidden when is_present / is_empty -->
          <template v-if="needsValue(cond)">
            <!-- number input -->
            <input
              v-if="attrDef(cond.attribute_key).inputType === 'number'"
              type="number"
              min="0"
              class="flex-1 h-8 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
              :value="cond.values"
              :placeholder="
                t('FLOW_BUILDER.ACTION_FORMS.CONDITION.QUANTITY_PLACEHOLDER')
              "
              @input="patch(i, { values: $event.target.value })"
            />

            <!-- text input -->
            <input
              v-else-if="
                ['text', 'custom_attr', 'dynamic_variable'].includes(
                  attrDef(cond.attribute_key).inputType
                )
              "
              type="text"
              class="flex-1 h-8 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
              :value="cond.values"
              :placeholder="t('FLOW_BUILDER.COMMON.VALUE_PLACEHOLDER')"
              @input="patch(i, { values: $event.target.value })"
            />

            <!-- multiSelect -->
            <div
              v-else-if="
                attrDef(cond.attribute_key).inputType === 'multiSelect'
              "
              class="flex-1"
            >
              <TagMultiSelectComboBox
                :options="
                  valOptions(cond.attribute_key).map(o => ({
                    value: String(o.id),
                    label: o.name,
                  }))
                "
                :model-value="
                  Array.isArray(cond.values) ? cond.values.map(String) : []
                "
                :placeholder="
                  t('FLOW_BUILDER.ACTION_FORMS.CONDITION.SELECT_PLACEHOLDER')
                "
                @update:model-value="v => patch(i, { values: v })"
              />
            </div>

            <!-- product select -->
            <div
              v-else-if="
                attrDef(cond.attribute_key).inputType === 'product_select'
              "
              class="multiselect-wrap--small w-full"
            >
              <MultiselectDropdown
                :options="catalogProducts"
                :selected-item="
                  catalogProducts.find(p => p.id === cond.values) ?? null
                "
                :multiselector-placeholder="
                  t('FLOW_BUILDER.ACTION_FORMS.CONDITION.SELECT_PRODUCT')
                "
                :input-placeholder="
                  t('FLOW_BUILDER.ACTION_FORMS.CONDITION.SEARCH_PRODUCT')
                "
                :has-thumbnail="false"
                @select="v => patch(i, { values: v?.id ?? '' })"
                @search-change="q => loadCatalogProducts(q)"
              />
            </div>

            <!-- single select -->
            <div v-else class="multiselect-wrap--small flex-1">
              <MultiselectDropdown
                :options="valOptions(cond.attribute_key)"
                :selected-item="selectedVal(cond)"
                :multiselector-placeholder="
                  t('FLOW_BUILDER.COMMON.VALUE_PLACEHOLDER')
                "
                :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
                :has-thumbnail="false"
                @select="v => patch(i, { values: v?.id ?? '' })"
              />
            </div>
          </template>
        </div>
      </div>
    </template>

    <Button
      ghost
      slate
      sm
      icon="i-lucide-plus"
      :label="t('FLOW_BUILDER.ACTION_FORMS.CONDITION.ADD_CONDITION')"
      class="self-start"
      @click="addCondition"
    />
  </div>
</template>
